export type Point = {
  x: number;
  y: number;
};

export type CableSurface = "front" | "rear" | "loose";

export type CableAnchor = Point & {
  surface: CableSurface;
};

export type CableRouteKind =
  "front-front" | "front-rear" | "rear-rear" | "front-loose" | "rear-loose" | "loose-loose";

export type CableRoute = {
  path: string;
  kind: CableRouteKind;
  geometryPoints: Point[];
  labelPoint: Point;
  looseDirectionA?: Point;
  looseDirectionB?: Point;
};

type Cubic = {
  start: Point;
  controlA: Point;
  controlB: Point;
  end: Point;
};

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function add(point: Point, vector: Point, distance: number): Point {
  return {
    x: point.x + vector.x * distance,
    y: point.y + vector.y * distance,
  };
}

function invert(vector: Point): Point {
  return { x: -vector.x, y: -vector.y };
}

function normalise(vector: Point, fallback: Point = { x: 0, y: 1 }): Point {
  const length = Math.hypot(vector.x, vector.y);
  return length < 0.001 ? fallback : { x: vector.x / length, y: vector.y / length };
}

function directionBetween(a: Point, b: Point, fallback?: Point) {
  return normalise({ x: b.x - a.x, y: b.y - a.y }, fallback);
}

function outwardDirection(surface: Exclude<CableSurface, "loose">): Point {
  return surface === "front" ? { x: 0, y: 1 } : { x: 0, y: -1 };
}

function pointOnCubic(curve: Cubic, t: number): Point {
  const inverse = 1 - t;
  return {
    x:
      inverse ** 3 * curve.start.x +
      3 * inverse ** 2 * t * curve.controlA.x +
      3 * inverse * t ** 2 * curve.controlB.x +
      t ** 3 * curve.end.x,
    y:
      inverse ** 3 * curve.start.y +
      3 * inverse ** 2 * t * curve.controlA.y +
      3 * inverse * t ** 2 * curve.controlB.y +
      t ** 3 * curve.end.y,
  };
}

function tangentOnCubic(curve: Cubic, t: number): Point {
  const inverse = 1 - t;
  return normalise({
    x:
      3 * inverse ** 2 * (curve.controlA.x - curve.start.x) +
      6 * inverse * t * (curve.controlB.x - curve.controlA.x) +
      3 * t ** 2 * (curve.end.x - curve.controlB.x),
    y:
      3 * inverse ** 2 * (curve.controlA.y - curve.start.y) +
      6 * inverse * t * (curve.controlB.y - curve.controlA.y) +
      3 * t ** 2 * (curve.end.y - curve.controlB.y),
  });
}

function offsetLabel(point: Point, tangent: Point): Point {
  let normal = { x: -tangent.y, y: tangent.x };
  if (normal.y < 0) normal = invert(normal);
  return add(point, normal, 14);
}

function routeKind(a: CableSurface, b: CableSurface): CableRouteKind {
  if (a === "loose" && b === "loose") return "loose-loose";
  if (a === "loose" || b === "loose") {
    const fixed = a === "loose" ? b : a;
    return fixed === "rear" ? "rear-loose" : "front-loose";
  }
  if (a === b) return a === "rear" ? "rear-rear" : "front-front";
  return "front-rear";
}

function format(value: number) {
  return Number(value.toFixed(2));
}

function move(point: Point) {
  return `M ${format(point.x)} ${format(point.y)}`;
}

function line(point: Point) {
  return `L ${format(point.x)} ${format(point.y)}`;
}

function cubic(curve: Cubic) {
  return `C ${format(curve.controlA.x)} ${format(curve.controlA.y)}, ${format(
    curve.controlB.x,
  )} ${format(curve.controlB.y)}, ${format(curve.end.x)} ${format(curve.end.y)}`;
}

function sameSurfaceRoute(
  a: CableAnchor,
  b: CableAnchor,
  kind: "front-front" | "rear-rear",
  sequence: number,
): CableRoute {
  const distance = Math.hypot(b.x - a.x, b.y - a.y);
  const exit = outwardDirection(kind === "front-front" ? "front" : "rear");
  const leadLength = clamp(distance * 0.1, 6, 18);
  const leadA = add(a, exit, leadLength);
  const leadB = add(b, exit, leadLength);
  const horizontalDistance = Math.abs(b.x - a.x);
  const verticalDistance = Math.abs(b.y - a.y);
  const laneDepth = ((Math.max(1, sequence) - 1) % 4) * 10;
  const depth =
    clamp(34 + horizontalDistance * 0.12 + verticalDistance * 0.06, 42, 135) + laneDepth;
  const curve: Cubic = {
    start: leadA,
    controlA: add(leadA, exit, depth),
    controlB: add(leadB, exit, depth),
    end: leadB,
  };

  return {
    kind,
    geometryPoints: [a, leadA, curve.controlA, curve.controlB, leadB, b],
    labelPoint: offsetLabel(pointOnCubic(curve, 0.5), tangentOnCubic(curve, 0.5)),
    path: `${move(a)} ${line(leadA)} ${cubic(curve)} ${line(b)}`,
  };
}

function frontRearRoute(a: CableAnchor, b: CableAnchor, sequence: number): CableRoute {
  const front = (a.surface === "front" ? a : b) as CableAnchor & { surface: "front" };
  const rear = (a.surface === "rear" ? a : b) as CableAnchor & { surface: "rear" };
  const leadLength = clamp(Math.hypot(rear.x - front.x, rear.y - front.y) * 0.1, 6, 18);
  const frontLead = add(front, outwardDirection("front"), leadLength);
  const rearLead = add(rear, outwardDirection("rear"), leadLength);
  const gap = Math.max(0, rearLead.y - frontLead.y);
  const laneDepth = ((Math.max(1, sequence) - 1) % 5) * 6;
  const handle = Math.min(150, Math.max(18, gap * 0.32 + laneDepth), gap * 0.45);
  const curve: Cubic = {
    start: frontLead,
    controlA: { x: frontLead.x, y: frontLead.y + handle },
    controlB: { x: rearLead.x, y: rearLead.y - handle },
    end: rearLead,
  };

  return {
    kind: "front-rear",
    geometryPoints: [front, frontLead, curve.controlA, curve.controlB, rearLead, rear],
    labelPoint: offsetLabel(pointOnCubic(curve, 0.5), tangentOnCubic(curve, 0.5)),
    path: `${move(front)} ${line(frontLead)} ${cubic(curve)} ${line(rear)}`,
  };
}

function fixedLooseRoute(a: CableAnchor, b: CableAnchor, kind: CableRouteKind): CableRoute {
  const fixed = a.surface === "loose" ? b : a;
  const loose = a.surface === "loose" ? a : b;
  const fixedSurface = fixed.surface as Exclude<CableSurface, "loose">;
  const exit = outwardDirection(fixedSurface);
  const distance = Math.hypot(loose.x - fixed.x, loose.y - fixed.y);
  const leadLength = clamp(distance * 0.1, 6, 18);
  const fixedLead = add(fixed, exit, leadLength);
  const backIntoCable = directionBetween(loose, fixedLead, invert(exit));
  const looseLead = add(loose, backIntoCable, leadLength);
  const innerDistance = Math.hypot(looseLead.x - fixedLead.x, looseLead.y - fixedLead.y);
  const fixedHandle = clamp(innerDistance * 0.4, 18, 280);
  const looseHandle = clamp(innerDistance * 0.3, 14, 220);
  const curve: Cubic = {
    start: fixedLead,
    controlA: add(fixedLead, exit, fixedHandle),
    controlB: add(looseLead, backIntoCable, looseHandle),
    end: looseLead,
  };
  const tipDirection = directionBetween(looseLead, loose, invert(backIntoCable));

  return {
    kind,
    geometryPoints: [fixed, fixedLead, curve.controlA, curve.controlB, looseLead, loose],
    labelPoint: offsetLabel(pointOnCubic(curve, 0.5), tangentOnCubic(curve, 0.5)),
    looseDirectionA: a.surface === "loose" ? tipDirection : undefined,
    looseDirectionB: b.surface === "loose" ? tipDirection : undefined,
    path: `${move(fixed)} ${line(fixedLead)} ${cubic(curve)} ${line(loose)}`,
  };
}

function looseLooseRoute(a: CableAnchor, b: CableAnchor): CableRoute {
  const direction = directionBetween(a, b, { x: 1, y: 0 });
  const distance = Math.hypot(b.x - a.x, b.y - a.y);
  const handle = clamp(distance * 0.3, 12, 100);
  const curve: Cubic = {
    start: a,
    controlA: add(a, direction, handle),
    controlB: add(b, direction, -handle),
    end: b,
  };

  return {
    kind: "loose-loose",
    geometryPoints: [a, curve.controlA, curve.controlB, b],
    labelPoint: offsetLabel(pointOnCubic(curve, 0.5), tangentOnCubic(curve, 0.5)),
    looseDirectionA: invert(direction),
    looseDirectionB: direction,
    path: `${move(a)} ${cubic(curve)}`,
  };
}

export function routeCable(a: CableAnchor, b: CableAnchor, sequence = 1): CableRoute {
  const kind = routeKind(a.surface, b.surface);

  if (kind === "front-front" || kind === "rear-rear") {
    return sameSurfaceRoute(a, b, kind, sequence);
  }
  if (kind === "front-rear") return frontRearRoute(a, b, sequence);
  if (kind === "loose-loose") return looseLooseRoute(a, b);
  return fixedLooseRoute(a, b, kind);
}
